import React, { useState, useEffect } from 'react';
import { Users, FileText, Map, CheckCircle, XCircle, Search, Activity, Image as ImageIcon, RefreshCw, Trash2, Edit, Save, Zap, Plus, MapPin } from 'lucide-react';
import Card from '../components/ui/Card';
import ApiService from '../services/apiService';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Fix leaflet icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export const getMarkerIcon = (id) => {
    const colors = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
    const color = colors[(id || 0) % colors.length];
    return L.divIcon({
        className: 'custom-pin',
        html: `<div style="background-color: ${color}; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.5);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
    });
};

const AdminOverview = ({ adminId, t, isRTL }) => {
    const [stats, setStats] = useState({ total_users: 0, revenue: 0, total_ai_runs: 0 });
    const [refreshing, setRefreshing] = useState(false);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const data = await ApiService.get(`/admin/analytics?user_id=${adminId}`);
            setStats(data);
        } catch (e) { console.error(e); }
        setRefreshing(false);
    };

    useEffect(() => { fetchData(); }, [adminId]);

    return (

        <div className="space-y-4">

            <div className="flex justify-end">

                {/* Updated button to support light mode with geo colors */}

                <button onClick={fetchData} className="flex items-center gap-2 bg-white dark:bg-geo-navy hover:bg-gray-50 dark:hover:bg-slate-800 text-geo-navy dark:text-slate-300 px-3 py-1.5 rounded text-sm transition-colors border border-geo-navy/10 dark:border-white/10 focus:outline-none shadow-sm">

                    <RefreshCw size={16} className={refreshing ? 'animate-spin text-geo-olive' : ''} />

                    {t.admin?.common?.refresh || "Refresh"}

                </button>

            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* Removed bg-slate-800 border-slate-700 so Card.js defaults work! */}

                <Card className="p-6 flex flex-col justify-center items-center">

                    <Users size={32} className="text-blue-500 mb-2" />

                    <p className="text-gray-600 dark:text-slate-400 text-sm">{t.admin?.overview?.totalUsers || "Total Users"}</p>

                    <p className="text-3xl text-geo-navy dark:text-white font-bold">{stats.total_users}</p>

                </Card>



                <Card className="p-6 flex flex-col justify-center items-center">

                    <FileText size={32} className="text-emerald-500 mb-2" />

                    <p className="text-gray-600 dark:text-slate-400 text-sm">{t.admin?.overview?.revenue || "Total Revenue"}</p>

                    <p className="text-3xl text-geo-navy dark:text-white font-bold">${stats.revenue}</p>

                </Card>



                <Card className="p-6 flex flex-col justify-center items-center">

                    <Activity size={32} className="text-purple-500 mb-2" />

                    <p className="text-gray-600 dark:text-slate-400 text-sm">{t.admin?.overview?.totalAiRuns || "Total AI Actions"}</p>

                    <p className="text-3xl text-geo-navy dark:text-white font-bold">{stats.total_ai_runs}</p>

                </Card>

            </div>

        </div>

    );
};

const MapClickHandler = ({ onClick }) => {
    useMapEvents({
        click(e) { onClick(e.latlng); }
    });
    return null;
};

const AdminIoTMapView = ({ adminId, t, isRTL }) => {
    const [devices, setDevices] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [history, setHistory] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ device_name: '', installed_location: '', latitude: 30.095, longitude: 31.315, soil_topic: '', leaf_topic: '', user_ids: [], farm_id: 1 });
    const [testingMqtt, setTestingMqtt] = useState(false);
    const [testResults, setTestResults] = useState(null);

    const handleTestMqtt = async () => {
        setTestingMqtt(true);
        setTestResults(null);
        try {
            const res = await ApiService.post('/admin/test-mqtt', {
                soil_topic: formData.soil_topic || "",
                leaf_topic: formData.leaf_topic || "",
                admin_id: adminId
            });
            if (res.status === 'success') {
                setTestResults(res.results.length > 0 ? res.results : [{ topic: 'Connected', payload: 'No payloads arrived in 2.5s window.' }]);
            } else {
                alert(res.message || "Failed to connect to broker");
            }
        } catch (e) { alert("Execution error during connection test"); }
        setTestingMqtt(false);
    };

    const fetchDevices = async () => {
        setRefreshing(true);
        try {
            const data = await ApiService.get(`/devices?user_id=${adminId}`);
            setDevices(Array.isArray(data) ? data : []);

            const usersData = await ApiService.get(`/admin/users?admin_id=${adminId}`);
            setAllUsers(Array.isArray(usersData) ? usersData : []);
        } catch (e) {
            console.error(e);
        }
        setRefreshing(false);
    };

    useEffect(() => { fetchDevices(); }, [adminId]);

    const fetchHistory = async (deviceId) => {
        try {
            const data = await ApiService.get(`/iot-history/${deviceId}`);
            if (data.status === 'success') {
                setHistory(data.data.reverse()); // Chronological for charts
            }
        } catch (e) { console.error(e); }
    };

    const handleSaveDevice = async () => {
        try {
            const parsedTopics = [formData.soil_topic, formData.leaf_topic].filter(Boolean);

            const payload = {
                admin_id: adminId,
                user_ids: Array.isArray(formData.user_ids) ? formData.user_ids : [parseInt(formData.user_ids) || 1],
                farm_id: parseInt(formData.farm_id),
                device_name: formData.device_name,
                installed_location: formData.installed_location,
                latitude: parseFloat(formData.latitude),
                longitude: parseFloat(formData.longitude),
                mqtt_topics: parsedTopics
            };

            if (selectedDevice && selectedDevice.id) {
                await ApiService.put(`/admin/devices/${selectedDevice.id}`, payload);
            } else {
                await ApiService.post('/admin/devices', payload);
            }
            setIsEditing(false);
            setSelectedDevice(null);
            fetchDevices();
        } catch (e) { alert("Failed to save device."); }
    };

    const handleDeleteDevice = async (id) => {
        if (!window.confirm("Delete this device entirely?")) return;
        try {
            await ApiService.delete(`/admin/devices/${id}`, { admin_id: adminId });
            setSelectedDevice(null);
            setIsEditing(false);
            fetchDevices();
        } catch (e) { alert("Failed to delete"); }
    };

    const handleMapClick = (latlng) => {
        if (isEditing) {
            setFormData({ ...formData, latitude: latlng.lat, longitude: latlng.lng });
        }
    };

    const startEdit = (dev = null) => {
        setTestResults(null);
        if (dev) {
            setSelectedDevice(dev);
            let soil_topic = "";
            let leaf_topic = "";
            try {
                const topics = typeof dev.mqtt_topics === 'string' ? JSON.parse(dev.mqtt_topics) : (dev.mqtt_topics || []);
                soil_topic = topics[0] || "";
                leaf_topic = topics[1] || "";
            } catch (e) { }

            setFormData({
                device_name: dev.device_name || '', installed_location: dev.installed_location || '',
                latitude: dev.latitude || 30.095, longitude: dev.longitude || 31.315,
                soil_topic, leaf_topic,
                user_ids: dev.assigned_user_ids || [dev.user_id || 1], farm_id: dev.farm_id || 1
            });
        } else {
            setSelectedDevice(null);
            setFormData({ device_name: 'New Node', installed_location: 'Greenhouse A', latitude: 30.095, longitude: 31.315, soil_topic: '', leaf_topic: '', user_ids: [1], farm_id: 1 });
        }
        setIsEditing(true);
    };

    return (
        <div className="flex flex-col h-full gap-4">
            <div className="flex justify-between items-center bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-4 rounded-xl shadow-sm transition-colors">
                <div>
                    <h3 className="text-xl font-bold text-geo-olive">Device Fleet Editor</h3>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Map new devices, link Users, and edit topics.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => startEdit(null)} className="bg-geo-olive hover:bg-green-700 text-white font-bold px-4 py-2 rounded text-sm shadow-lg border border-geo-olive/50 transition-colors">
                        + Add Device
                    </button>
                    <button onClick={fetchDevices} className="flex items-center gap-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-geo-navy dark:text-slate-300 px-3 py-1.5 rounded text-sm transition-colors border border-gray-200 dark:border-slate-700">
                        <RefreshCw size={16} className={refreshing ? 'animate-spin text-geo-olive' : ''} />
                    </button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 h-full">
                {/* Map Area */}
                <div className="lg:w-2/3 h-[500px] border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-xl z-0 relative transition-colors bg-black">
                    <MapContainer center={[26.8206, 30.8025]} zoom={6} scrollWheelZoom={true} className="w-full h-full cursor-crosshair">
                        <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution='&copy; ESRI' />
                        <MapClickHandler onClick={handleMapClick} />
                        {devices.map(device => (
                            device.latitude && device.longitude ? (
                                <Marker
                                    key={device.id}
                                    position={[device.latitude, device.longitude]}
                                    icon={getMarkerIcon(device.id)}
                                    eventHandlers={{
                                        click: () => {
                                            if (!isEditing) {
                                                setSelectedDevice(device);
                                                fetchHistory(device.id);
                                            }
                                        },
                                    }}
                                >
                                    <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent className="font-bold text-xs bg-transparent text-geo-olive border-0 shadow-none">
                                        {device.device_name || `Device #${device.id}`}
                                    </Tooltip>
                                    <Popup>
                                        <div className="text-slate-800">
                                            <strong>{device.device_name || `Device #${device.id}`}</strong>
                                            <br />{device.installed_location}
                                            <div className="mt-2 text-center border-t pt-2">
                                                <button onClick={() => { startEdit(device); }} className="text-xs bg-geo-olive text-white px-2 py-1 rounded">Edit</button>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ) : null
                        ))}
                        {/* Editor Active Point Preview */}
                        {isEditing && formData.latitude && (
                            <Marker position={[formData.latitude, formData.longitude]} opacity={0.6}>
                                <Popup>New Location Pin</Popup>
                            </Marker>
                        )}
                    </MapContainer>
                    {isEditing && (
                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/60 backdrop-blur border border-geo-olive text-white font-bold px-4 py-2 rounded-lg z-[1000] drop-shadow-2xl pointer-events-none fade-in transition-colors text-shadow-sm">
                            Click anywhere on the map to drop the device pin.
                        </div>
                    )}
                </div>

                {/* Editor / Info Pane */}
                <div className="lg:w-1/3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-4 rounded-xl flex flex-col min-h-[500px] shadow-sm transition-colors">
                    {isEditing ? (
                        <div className="flex flex-col h-full fade-in">
                            <h4 className="text-lg font-bold text-geo-navy dark:text-white mb-4 border-b border-gray-200 dark:border-slate-800 pb-2 flex justify-between transition-colors">
                                {selectedDevice ? 'Edit Device' : 'Deploy New Device'}
                            </h4>
                            <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-1">
                                <div><label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase">Name</label><input type="text" value={formData.device_name} onChange={e => setFormData({ ...formData, device_name: e.target.value })} className="w-full bg-gray-50 dark:bg-slate-800 text-geo-navy dark:text-white border border-gray-200 dark:border-slate-700 p-2 rounded focus:border-geo-olive outline-none transition-colors" /></div>
                                <div><label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase">Location</label><input type="text" value={formData.installed_location} onChange={e => setFormData({ ...formData, installed_location: e.target.value })} className="w-full bg-gray-50 dark:bg-slate-800 text-geo-navy dark:text-white border border-gray-200 dark:border-slate-700 p-2 rounded focus:border-geo-olive outline-none transition-colors" /></div>

                                <div className="grid grid-cols-2 gap-2 mb-2">
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase mb-1 block">Target User IDs</label>
                                        <select
                                            multiple
                                            value={formData.user_ids}
                                            onChange={e => {
                                                const vals = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                                setFormData({ ...formData, user_ids: vals });
                                            }}
                                            className="w-full bg-gray-50 dark:bg-slate-800 text-geo-navy dark:text-white border border-gray-200 dark:border-slate-700 p-2 rounded focus:border-geo-olive outline-none font-mono text-xs h-24 custom-scrollbar transition-colors"
                                        >
                                            {allUsers.map(u => (
                                                <option key={u.id} value={u.id}>{u.id} - {u.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase mb-1 block">Target Farm ID</label>
                                        <input type="number" value={formData.farm_id} onChange={e => setFormData({ ...formData, farm_id: e.target.value })} className="w-full bg-gray-50 dark:bg-slate-800 text-geo-navy dark:text-white border border-gray-200 dark:border-slate-700 p-2 rounded focus:border-geo-olive outline-none font-mono h-10 transition-colors" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2">
                                    <div><label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase">Lat</label><input type="number" step="0.0001" value={formData.latitude} readOnly className="w-full bg-gray-100 dark:bg-slate-900/50 text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-slate-700 p-2 rounded outline-none font-mono transition-colors" /></div>
                                    <div><label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase">Lng</label><input type="number" step="0.0001" value={formData.longitude} readOnly className="w-full bg-gray-100 dark:bg-slate-900/50 text-gray-500 dark:text-slate-400 border border-gray-200 dark:border-slate-700 p-2 rounded outline-none font-mono transition-colors" /></div>
                                </div>

                                <div>
                                    <label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase">Soil Sensor Topic</label>
                                    <input type="text" value={formData.soil_topic} onChange={e => setFormData({ ...formData, soil_topic: e.target.value })} className="w-full bg-gray-50 dark:bg-slate-800 text-geo-olive border border-gray-200 dark:border-slate-700 p-2 rounded font-mono text-xs mb-2 focus:border-geo-olive outline-none transition-colors" placeholder="i4Technology/.../SevenReadingsinOneSensor" />

                                    <label className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase">Leaf Sensor Topic</label>
                                    <input type="text" value={formData.leaf_topic} onChange={e => setFormData({ ...formData, leaf_topic: e.target.value })} className="w-full bg-gray-50 dark:bg-slate-800 text-geo-olive border border-gray-200 dark:border-slate-700 p-2 rounded font-mono text-xs mb-3 focus:border-geo-olive outline-none transition-colors" placeholder="i4Technology/.../LeafSensor" />

                                    <button onClick={handleTestMqtt} disabled={testingMqtt} className="w-full bg-geo-navy dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-600 border border-transparent dark:border-slate-600 text-white font-bold py-1.5 px-3 rounded shadow transition-colors flex justify-center items-center gap-2">
                                        {testingMqtt ? <Activity size={16} className="animate-spin" /> : <Activity size={16} />} Test Connection
                                    </button>

                                    {testResults && (
                                        <div className="mt-2 bg-gray-100 dark:bg-black border border-gray-200 dark:border-slate-700 p-2 rounded text-xs font-mono max-h-32 overflow-y-auto transition-colors">
                                            {testResults.map((tr, idx) => (
                                                <div key={idx} className="border-b border-gray-300 dark:border-slate-800 pb-1 mb-1 last:border-0 last:mb-0 last:pb-0">
                                                    <span className="text-geo-olive">{tr.topic}</span><br />
                                                    <span className="text-geo-navy dark:text-slate-300">Payload: {tr.payload}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-800 flex flex-col gap-2 shrink-0 transition-colors">
                                <button onClick={handleSaveDevice} className="w-full flex justify-center items-center gap-2 bg-geo-olive hover:bg-green-700 text-white font-bold p-2 rounded shadow-lg transition-colors">
                                    <Save size={18} /> Save Device Configuration
                                </button>
                                {selectedDevice && (
                                    <button onClick={() => handleDeleteDevice(selectedDevice.id)} className="w-full flex justify-center items-center gap-2 border border-rose-500/50 text-rose-500 dark:text-rose-400 hover:bg-rose-500/10 font-bold p-2 rounded transition-colors">
                                        <Trash2 size={18} /> Purge Device
                                    </button>
                                )}
                                <button onClick={() => setIsEditing(false)} className="w-full text-gray-500 dark:text-slate-400 hover:text-geo-navy dark:hover:text-white p-2 transition-colors">Cancel</button>
                            </div>
                        </div>
                    ) : selectedDevice ? (
                        <div className="flex flex-col h-full fade-in">
                            <h4 className="text-lg font-bold text-geo-navy dark:text-white mb-4 border-b border-gray-200 dark:border-slate-800 pb-2 transition-colors">Reading: {selectedDevice.device_name}</h4>
                            <div className="flex-1 bg-gray-50 dark:bg-slate-950 border border-gray-200 dark:border-slate-800 rounded-lg p-2 overflow-hidden mb-4 relative transition-colors">
                                {history.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={history}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#cbd5e1" className="dark:stroke-slate-800" />
                                            <XAxis dataKey="timestamp" stroke="#64748b" tickFormatter={(t) => new Date(t).toLocaleTimeString()} hide={true} />
                                            <Tooltip contentStyle={{ backgroundColor: '#1B263B', border: 'none', color: '#fff', borderRadius: '8px' }} />
                                            <Line type="monotone" dataKey="temperature" stroke="#ef4444" dot={false} strokeWidth={2} />
                                            <Line type="monotone" dataKey="humidity" stroke="#3b82f6" dot={false} strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-gray-500 dark:text-slate-600">No Historical Data</div>
                                )}
                            </div>
                            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700 mt-auto shrink-0 transition-colors">
                                <p className="text-xs text-gray-500 dark:text-slate-400 font-bold uppercase tracking-widest mb-2 border-b border-gray-200 dark:border-slate-700 pb-1 transition-colors">Assignment Link</p>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-geo-navy dark:text-slate-300">Bound to User ID:</span>
                                    <span className="text-geo-olive dark:text-emerald-400 font-mono bg-white dark:bg-slate-900 border border-gray-200 dark:border-transparent px-2 py-0.5 rounded transition-colors">{selectedDevice.user_id}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm mt-1">
                                    <span className="text-geo-navy dark:text-slate-300">Bound to Farm ID:</span>
                                    <span className="text-geo-olive dark:text-emerald-400 font-mono bg-white dark:bg-slate-900 border border-gray-200 dark:border-transparent px-2 py-0.5 rounded transition-colors">{selectedDevice.farm_id}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col justify-center items-center h-full text-gray-400 dark:text-slate-500">
                            <Map size={48} className="mb-4 opacity-20" />
                            <p>Select a node on the map to view data.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
const AdminApprovalsView = ({ adminId, t, isRTL }) => {
    const [pending, setPending] = useState([]);
    const [refreshing, setRefreshing] = useState(false);

    const fetchPending = async () => {
        setRefreshing(true);
        try {
            const data = await ApiService.get(`/admin/payments/pending?admin_id=${adminId}`);
            setPending(data);
        } catch (e) { console.error(e); }
        setRefreshing(false);
    };

    useEffect(() => { fetchPending(); }, [adminId]);

    const handleAction = async (billingId, action) => {
        try {
            await ApiService.put(`/admin/payments/${billingId}/status`, { admin_id: adminId, status: action });
            setPending(pending.filter(p => p.id !== billingId));
        } catch (e) {
            console.error(e);
            alert("Failed to process payment status.");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-geo-olive">Pending Subscription Approvals</h3>
                <button onClick={fetchPending} className="flex items-center gap-2 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-geo-navy dark:text-slate-300 px-3 py-1.5 rounded text-sm transition-colors border border-gray-200 dark:border-slate-700 focus:outline-none shadow-sm">
                    <RefreshCw size={16} className={refreshing ? 'animate-spin text-geo-olive' : ''} />
                    Refresh
                </button>
            </div>
            {pending.length === 0 ? (
                <p className="text-gray-500 dark:text-slate-400">No pending approvals.</p>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {pending.map(p => (
                        <Card key={p.id} className="flex flex-col md:flex-row items-center justify-between p-4 transition-colors">
                            <div>
                                <p className="text-geo-navy dark:text-white font-bold">{p.user?.name} ({p.user?.email})</p>
                                <p className="text-gray-500 dark:text-slate-400 text-sm">Amount: ${p.amount} | Method: {p.payment_method}</p>
                                <p className="text-geo-olive dark:text-emerald-400 text-xs">Date: {new Date(p.payment_date).toLocaleDateString()}</p>
                            </div>
                            <div className="flex flex-col items-center gap-2 mt-4 md:mt-0">
                                {p.transfer_screenshot_path ? (
                                    <a href={`http://localhost:8009/${p.transfer_screenshot_path}`} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 border border-blue-600/50 dark:border-blue-400/50 rounded px-2 py-1 text-xs hover:bg-blue-50 dark:hover:bg-blue-400/10 transition-colors">View Receipt</a>
                                ) : (
                                    <span className="text-gray-400 dark:text-slate-500 text-xs">No Receipt Uploaded</span>
                                )}
                                <div className="flex gap-2">
                                    <button onClick={() => handleAction(p.id, 'Approved')} className="bg-geo-olive hover:bg-green-700 text-white px-4 py-2 rounded font-bold text-sm flex items-center gap-1 transition-colors"><CheckCircle size={16} /> Approve</button>
                                    <button onClick={() => handleAction(p.id, 'Rejected')} className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded font-bold text-sm flex items-center gap-1 transition-colors"><XCircle size={16} /> Reject</button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

const isImageUrl = (val) => {
    if (typeof val !== 'string') return false;
    if (val.startsWith('data:image')) return true;
    if (val.match(/^https?:\/\/.*\.(png|jpe?g|webp|gif|svg)$/i)) return true;
    if (val.includes('/static/uploads/') || val.includes('/static/outputs/') || val.includes('/static/satellite_plots/')) return true;
    return false;
};

const getFullImageUrl = (url) => {
    if (!url) return '';
    
    // 1. If the database accidentally saved the old localhost URL, force it to the live domain
    if (url.includes('127.0.0.1:8009') || url.includes('localhost:8009')) {
        let cleanPath = url.replace('http://127.0.0.1:8009', '').replace('http://localhost:8009', '');
        if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
        return `https://geonutria.ai${cleanPath}`;
    }

    // 2. If it's a valid external URL (like Google Profile pictures), let it pass
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    
    // 3. If it's a relative path from your backend, append the live domain NOT localhost
    if (url.startsWith('/static/') || url.startsWith('static/')) {
        const path = url.startsWith('/') ? url : `/${url}`;
        return `https://geonutria.ai${path}`;
    }
    
    return url;
};

const renderJsonWithImages = (data, prefixText, containerClass) => {
    let parsed = data;
    if (typeof data === 'string') {
        try { parsed = JSON.parse(data); } catch (e) { }
    }

    if (isImageUrl(parsed)) {
        return (
            <div className={containerClass}>
                <span className="font-bold mb-1 block text-current">{prefixText}</span>
                <a href={getFullImageUrl(parsed)} target="_blank" rel="noopener noreferrer" className="block w-fit mx-auto cursor-zoom-in mt-2">
                    <img src={getFullImageUrl(parsed)} alt="Output" className="w-full max-w-sm lg:max-w-md h-auto object-contain rounded border border-slate-700 bg-black/50 shadow-lg hover:border-emerald-500 transition-colors" />
                </a>
            </div>
        );
    }

    if (typeof parsed === 'object' && parsed !== null) {
        const images = [];
        const cleanObj = JSON.parse(JSON.stringify(parsed)); // Deep copy

        const extractImagesDeep = (obj) => {
            if (Array.isArray(obj)) {
                for (let i = 0; i < obj.length; i++) {
                    if (isImageUrl(obj[i])) {
                        images.push(obj[i]);
                        obj[i] = "[Image Previewed Below]";
                    } else if (typeof obj[i] === 'object' && obj[i] !== null) {
                        extractImagesDeep(obj[i]);
                    }
                }
            } else if (typeof obj === 'object' && obj !== null) {
                for (const [k, v] of Object.entries(obj)) {
                    if (isImageUrl(v)) {
                        images.push(v);
                        obj[k] = "[Image Previewed Below]";
                    } else if (typeof v === 'object' && v !== null) {
                        extractImagesDeep(v);
                    }
                }
            }
        };

        extractImagesDeep(cleanObj);

        return (
            <div className={containerClass}>
                <div className="break-all whitespace-pre-wrap font-mono relative">
                    <span className="font-bold text-current">{prefixText} </span>
                    {JSON.stringify(cleanObj, null, 2)}
                </div>
                {images.length > 0 && (
                    <div className="flex flex-col gap-4 mt-4 items-center w-full">
                        {images.map((img, i) => (
                            <a href={getFullImageUrl(img)} key={i} target="_blank" rel="noopener noreferrer" className="block cursor-zoom-in">
                                <img src={getFullImageUrl(img)} alt="Embedded preview" className="max-w-full lg:max-w-[600px] h-auto object-contain rounded-lg border border-slate-600 bg-black/60 shadow-xl hover:border-emerald-500 transition-colors" />
                            </a>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={`${containerClass} break-all whitespace-pre-wrap font-mono`}>
            <span className="font-bold text-current">{prefixText} </span>
            {JSON.stringify(parsed, null, 2)}
        </div>
    );
};

const UserDrillDown = ({ user, adminId, onBack, onRefresh, t, isRTL }) => {
    const { profile, logs, files } = user;
    const [permissions, setPermissions] = useState(profile?.permissions || {});
    const [credits, setCredits] = useState(profile?.ai_credits || 0);
    const [saving, setSaving] = useState(false);
    const [isSavingPerms, setIsSavingPerms] = useState(false);

    useEffect(() => {
        setPermissions(profile?.permissions || {});
        setCredits(profile?.ai_credits || 0);
    }, [profile]);

    const FEATURES = [
        "IoT Device Readings", "AI General Diagnosis", "Global Active Context",
        "Leaf Doctor", "Soil Analysis", "Crop Recommendation Engine",
        "Crop Yield Estimator", "Satellite Farm Analysis", "Aerial Palm Counting",
        "AI Consultant Smart Assistant", "Export Report", "Technical Support"
    ];

    const handleChangeState = (feature, val) => {
        setPermissions({ ...permissions, [feature]: val });
    };

    const handleSavePermissions = async () => {
        setIsSavingPerms(true);
        try {
            await ApiService.put(`/admin/users/${profile.id}/features`, { admin_id: adminId, features: permissions });
            alert("Permissions saved successfully!");
        } catch (e) { console.error("Toggle error", e); alert("Failed to save permissions"); }
        setIsSavingPerms(false);
    };

    const handleSaveCredits = async () => {
        setSaving(true);
        try {
            await ApiService.put(`/admin/users/${profile.id}/credits`, { admin_id: adminId, ai_credits: credits });
        } catch (e) { console.error("Credits error", e); }
        setSaving(false);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center bg-white dark:bg-slate-800/50 p-3 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm transition-colors">
                <button onClick={onBack} className="text-geo-olive dark:text-emerald-400 flex items-center gap-2 hover:text-green-700 dark:hover:text-emerald-300 transition-colors font-bold">
                    &larr; Back to Directory
                </button>
                <button onClick={onRefresh} className="flex items-center gap-2 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-geo-olive dark:text-emerald-400 px-3 py-1.5 rounded transition-colors border border-geo-olive/30 dark:border-emerald-500/50 focus:outline-none font-bold text-sm">
                    <RefreshCw size={16} /> Refresh Credits & Data
                </button>
            </div>

            {/* Section A - User Profile */}
            <Card className="p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-geo-olive/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center relative z-10 gap-6">
                    <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-geo-olive/10 dark:bg-emerald-900/50 rounded-full border-2 border-geo-olive flex items-center justify-center text-geo-olive dark:text-emerald-400 text-3xl font-bold">
                            {profile?.name ? profile.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-geo-navy dark:text-white tracking-tight">{profile?.name || 'Unknown User'}</h2>
                            <p className="text-gray-600 dark:text-slate-400 flex items-center gap-2 mt-1">
                                <span>{profile?.email || 'No email provided'}</span>
                                <span className="text-gray-400 dark:text-slate-600">•</span>
                                <span>{profile?.mobile || 'No phone provided'}</span>
                            </p>
                            <div className="flex gap-3 mt-3">
                                <span className="bg-gray-100 dark:bg-slate-800 text-geo-navy dark:text-slate-300 text-xs px-3 py-1 rounded-full border border-gray-200 dark:border-slate-700">Role: {profile?.role || 'User'}</span>
                                <span className="bg-gray-100 dark:bg-slate-800 text-geo-navy dark:text-slate-300 text-xs px-3 py-1 rounded-full border border-gray-200 dark:border-slate-700">
                                    Joined: {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                        <div className="bg-gray-50 dark:bg-slate-800/80 rounded-xl p-4 border border-gray-200 dark:border-slate-700 text-center flex-1 md:w-48 transition-colors">
                            <p className="text-gray-500 dark:text-slate-400 text-xs uppercase tracking-wider mb-1">Subscription Plan</p>
                            <p className="text-geo-olive dark:text-emerald-400 font-bold text-xl">{profile?.subscription_plan || 'Free Tier'}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-800/80 rounded-xl p-4 border border-gray-200 dark:border-slate-700 text-center flex-1 md:w-48 transition-colors">
                            <p className="text-gray-500 dark:text-slate-400 text-xs uppercase tracking-wider mb-1">AI Credits</p>
                            <div className="flex items-center justify-center gap-2 mt-1">
                                <input
                                    type="number"
                                    value={credits}
                                    onChange={e => setCredits(parseInt(e.target.value) || 0)}
                                    className="w-20 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded p-1 text-amber-600 dark:text-amber-400 font-bold text-center outline-none focus:border-amber-500 transition-colors"
                                />
                                <button onClick={handleSaveCredits} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white p-1 rounded text-xs px-2 transition-colors">
                                    {saving ? '...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Feature Toggles */}
            <Card className="p-6">
                <h3 className="text-xl font-bold text-geo-olive dark:text-emerald-400 mb-6 flex items-center gap-2 border-b border-gray-200 dark:border-slate-800 pb-4">
                    <CheckCircle className="text-geo-olive dark:text-emerald-500" /> Granular Access Control
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FEATURES.map(feat => {
                        const permObj = permissions[feat];
                        const val = (typeof permObj === 'object' && permObj !== null) ? permObj.state : (permObj || 'credit_based');
                        const cost = (typeof permObj === 'object' && permObj !== null) ? (permObj.cost !== undefined ? permObj.cost : 5) : 5;

                        let colorClass = val === 'unlimited' ? 'border-geo-olive text-geo-olive dark:text-emerald-400' :
                            val === 'blocked' ? 'border-rose-500 text-rose-600 dark:text-rose-400' :
                                'border-amber-500 text-amber-600 dark:text-amber-400';
                        return (
                            <div key={feat} className="flex flex-col gap-2 bg-gray-50 dark:bg-slate-800/50 p-3 rounded-lg border border-gray-200 dark:border-slate-700 transition-colors">
                                <span className="text-sm font-medium text-geo-navy dark:text-slate-200">{feat}</span>
                                <select
                                    className={`w-full bg-white dark:bg-slate-900 border ${colorClass} rounded p-1 text-sm outline-none transition-colors`}
                                    value={val}
                                    onChange={(e) => handleChangeState(feat, { state: e.target.value, cost })}
                                >
                                    <option value="credit_based">Credit Based</option>
                                    <option value="unlimited">Unlimited (Free)</option>
                                    <option value="blocked">Blocked</option>
                                </select>
                                {val === 'credit_based' && (
                                    <div className="flex items-center gap-2 mt-1 transition-all">
                                        <span className="text-xs text-gray-500 dark:text-slate-400">Cost:</span>
                                        <input type="number" min="0" value={cost} onChange={e => handleChangeState(feat, { state: val, cost: parseInt(e.target.value) || 0 })} className="w-16 bg-white dark:bg-slate-900 border border-amber-600/50 rounded p-1 text-xs text-amber-600 dark:text-amber-400 text-center font-bold outline-none focus:border-amber-500 transition-colors" />
                                        <span className="text-xs text-gray-500 dark:text-slate-400 italic">credits</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSavePermissions}
                        disabled={isSavingPerms}
                        className="flex items-center gap-2 bg-geo-olive hover:bg-green-700 font-bold px-6 py-2 rounded-lg text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-50"
                    >
                        {isSavingPerms ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                        Save Permissions
                    </button>
                </div>
            </Card>

            <div className="grid grid-cols-1 gap-6">
                {/* Section B - Activity Timeline */}
                <Card className="flex flex-col min-h-[800px] h-auto p-6">
                    <h3 className="text-xl font-bold text-geo-olive dark:text-emerald-400 mb-6 flex items-center gap-2 border-b border-gray-200 dark:border-slate-800 pb-4">
                        <Activity className="text-geo-olive dark:text-emerald-500" /> Activity Timeline
                    </h3>
                    <div className="relative pl-4 space-y-6 overflow-y-auto custom-scrollbar flex-1 pr-2">
                        <div className="absolute left-6 top-2 bottom-2 w-px bg-gray-300 dark:bg-slate-700"></div>

                        {logs && logs.length > 0 ? (
                            logs.map((log, index) => (
                                <div key={log.id || index} className="relative z-10 flex items-start gap-4 group">
                                    <div className="w-5 h-5 rounded-full bg-white dark:bg-slate-800 border-2 border-geo-olive mt-1 flex-shrink-0 group-hover:bg-geo-olive transition-colors"></div>
                                    <div className="flex-1 bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl border border-gray-200 dark:border-slate-700 group-hover:border-geo-olive/50 dark:group-hover:border-emerald-500/30 transition-colors shadow-sm">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className="font-bold text-geo-navy dark:text-slate-200">{log.action_type}</p>
                                            <span className="text-gray-500 dark:text-slate-500 text-xs font-mono bg-white dark:bg-slate-900/50 px-2 py-1 rounded border border-gray-200 dark:border-slate-700">
                                                {new Date(log.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        {log.feature_used && (
                                            <p className="text-geo-olive dark:text-emerald-400/80 text-sm mb-2 font-medium">{log.feature_used}</p>
                                        )}
                                        {log.inputs_json && renderJsonWithImages(log.inputs_json, "Input:", "mt-2 text-xs text-gray-600 dark:text-slate-400 bg-white dark:bg-slate-900/50 p-2 rounded border border-gray-200 dark:border-slate-700/50")}
                                        {log.outputs_json && renderJsonWithImages(log.outputs_json, "Output:", "mt-2 text-xs text-geo-olive dark:text-emerald-400/60 bg-green-50 dark:bg-emerald-900/20 p-2 rounded border border-green-200 dark:border-emerald-900/50")}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-slate-500 py-10 w-full text-center">
                                <Activity size={48} className="opacity-20 mb-4 mx-auto" />
                                <p>No activity recorded yet.</p>
                            </div>
                        )}
                    </div>
                </Card>

            </div>
        </div>
    );
};

const AdminUsersDirectory = ({ adminId, t, isRTL }) => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedUser, setSelectedUser] = useState(null); // The drill-down profile data
    const [refreshing, setRefreshing] = useState(false);

    const fetchUsers = async () => {
        setRefreshing(true);
        try {
            const data = await ApiService.get(`/admin/users?admin_id=${adminId}`);
            setUsers(data);
        } catch (e) { console.error(e); }
        setRefreshing(false);
    };

    useEffect(() => {
        if (!selectedUser) {
            fetchUsers();
        }
    }, [adminId, selectedUser]);

    const handleUserClick = async (userId) => {
        try {
            const data = await ApiService.get(`/admin/users/${userId}/profile?admin_id=${adminId}`);
            setSelectedUser(data);
        } catch (e) { console.error(e); }
    };

    if (selectedUser) {
        return <UserDrillDown user={selectedUser} adminId={adminId} onBack={() => setSelectedUser(null)} onRefresh={() => handleUserClick(selectedUser.profile.id)} t={t} isRTL={isRTL} />;
    }

    const filtered = users.filter(u => `${u.name} ${u.email}`.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <h3 className="text-xl font-bold text-geo-olive">{t.admin?.tabs?.users || "User Directory"}</h3>
                    <button onClick={fetchUsers} className="flex items-center gap-2 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 text-geo-navy dark:text-slate-300 px-3 py-1.5 rounded text-sm transition-colors border border-gray-200 dark:border-slate-700 focus:outline-none shrink-0 shadow-sm">
                        <RefreshCw size={16} className={refreshing ? 'animate-spin text-geo-olive' : ''} />
                        {t.admin?.common?.refresh || "Refresh"}
                    </button>
                </div>
                <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-400" size={18} />
                    <input type="text" placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-geo-navy dark:text-white focus:border-geo-olive outline-none transition-colors shadow-sm"
                    />
                </div>
            </div>

            <div className="overflow-x-auto bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl shadow-sm transition-colors">
                <table className="w-full text-left text-sm text-geo-navy dark:text-slate-300">
                    <thead className="bg-gray-50 dark:bg-slate-800 text-geo-olive border-b border-gray-200 dark:border-slate-700">
                        <tr>
                            <th className="p-4">{t.admin?.users?.name || "Name"}</th>
                            <th className="p-4">{t.admin?.users?.email || "Email"}</th>
                            <th className="p-4">{t.admin?.users?.role || "Role"}</th>
                            <th className="p-4">{t.admin?.users?.aiCredits || "Credits"}</th>
                            <th className="p-4">{t.admin?.users?.verified || "Verified"}</th>
                            <th className="p-4">{t.admin?.users?.joined || "Joined"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(u => (
                            <tr key={u.id} onClick={() => handleUserClick(u.id)} className="border-b border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
                                <td className="p-4 font-bold">{u.name}</td>
                                <td className="p-4">{u.email}</td>
                                <td className="p-4">{u.role}</td>
                                <td className="p-4"><span className="bg-geo-olive/10 dark:bg-geo-olive/20 text-geo-olive px-2 py-1 rounded font-medium">{u.ai_credits}</span></td>
                                <td className="p-4">{u.is_verified ? <CheckCircle className="text-geo-olive" size={16} /> : <XCircle className="text-gray-400 dark:text-slate-500" size={16} />}</td>
                                <td className="p-4 text-xs text-gray-500 dark:text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const LocationPicker = ({ onLocationSelect }) => {
    useMapEvents({
        click(e) {
            onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
        },
    });
    return null;
};

const AdminActuatorsView = ({ adminId, t, isRTL }) => {
    const [actuators, setActuators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [showForm, setShowForm] = useState(false);
    
    const [formData, setFormData] = useState({
        device_name: '',
        device_type: 'Pump',
        mqtt_publish_topic: '',
        mqtt_subscribe_topic: '',
        user_id: '',
        latitude: '',
        longitude: '',
        installed_location: ''
    });
    const [locationMode, setLocationMode] = useState('map'); // 'map' or 'manual'
    const [submitting, setSubmitting] = useState(false);
    
    // Default center for map picker
    const defaultCenter = [26.8206, 30.8025];

    const fetchActuators = async () => {
        setLoading(true);
        try {
            const data = await ApiService.get(`/admin/actuators?admin_id=${adminId}`);
            setActuators(data || []);
        } catch (e) {
            console.error('Failed to fetch actuators', e);
        }
        setLoading(false);
    };

    const fetchUsers = async () => {
        try {
            const data = await ApiService.get(`/admin/users?admin_id=${adminId}`);
            setUsers(data || []);
        } catch (e) {
            console.error('Failed to fetch users', e);
        }
    };

    useEffect(() => { 
        fetchActuators(); 
        fetchUsers();
    }, [adminId]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this actuator?")) return;
        try {
            await ApiService.delete(`/admin/actuators/${id}?admin_id=${adminId}`);
            setActuators(prev => prev.filter(a => a.id !== id));
        } catch (e) {
            console.error('Failed to delete actuator', e);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.user_id) {
            alert('Please select an assigned user.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                admin_id: adminId,
                device_name: formData.device_name,
                device_type: formData.device_type,
                mqtt_publish_topic: formData.mqtt_publish_topic,
                mqtt_subscribe_topic: formData.mqtt_subscribe_topic,
                user_ids: [parseInt(formData.user_id)],
                latitude: formData.latitude ? parseFloat(formData.latitude) : null,
                longitude: formData.longitude ? parseFloat(formData.longitude) : null,
                installed_location: formData.installed_location
            };
            const res = await ApiService.post('/admin/actuators', payload);
            if (res.status === 'success') {
                // Optimistic UI Update
                setActuators(prev => [{
                    id: res.actuator_id,
                    ...payload,
                    assigned_user_ids: payload.user_ids
                }, ...prev]);
                setShowForm(false);
                setFormData({
                    device_name: '', device_type: 'Pump', mqtt_publish_topic: '', mqtt_subscribe_topic: '',
                    user_id: '', latitude: '', longitude: '', installed_location: ''
                });
                // Small toast/alert could go here
            }
        } catch (error) {
            alert(error.message || 'Failed to create actuator');
        }
        setSubmitting(false);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-geo-navy dark:text-white">System Actuators</h3>
                <div className="flex gap-2">
                    <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 bg-geo-olive hover:bg-geo-olive/90 text-white px-4 py-2 rounded font-bold text-sm transition-colors shadow-sm">
                        <Plus size={16} /> {showForm ? 'Cancel' : 'Register New Actuator'}
                    </button>
                    <button onClick={fetchActuators} className="flex items-center gap-2 bg-white dark:bg-geo-navy hover:bg-gray-50 dark:hover:bg-slate-800 text-geo-navy dark:text-slate-300 px-3 py-1.5 rounded text-sm transition-colors border border-geo-navy/10 dark:border-white/10 shadow-sm">
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
                    </button>
                </div>
            </div>
            
            {showForm && (
                <Card className="!p-6 mb-6">
                    <h4 className="text-lg font-bold mb-4">Register New Actuator</h4>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1">Actuator Name</label>
                                <input required type="text" value={formData.device_name} onChange={e => setFormData({...formData, device_name: e.target.value})} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive" placeholder="e.g. Main Field Pump A" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1">Control Category</label>
                                <select required value={formData.device_type} onChange={e => setFormData({...formData, device_type: e.target.value})} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive">
                                    <option value="Pump">Water Pump</option>
                                    <option value="Valve">Valve</option>
                                    <option value="LED">LED Light</option>
                                    <option value="RGB">RGB Light</option>
                                    <option value="Fan">Fan</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1">MQTT Publish Topic (Commands)</label>
                                <input type="text" value={formData.mqtt_publish_topic} onChange={e => setFormData({...formData, mqtt_publish_topic: e.target.value})} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive font-mono" placeholder="farm/pump_a/cmd" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1">MQTT Subscribe Topic (Feedback)</label>
                                <input type="text" value={formData.mqtt_subscribe_topic} onChange={e => setFormData({...formData, mqtt_subscribe_topic: e.target.value})} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive font-mono" placeholder="farm/pump_a/state" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 mb-1">Assign to User</label>
                                <select required value={formData.user_id} onChange={e => setFormData({...formData, user_id: e.target.value})} className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive">
                                    <option value="">-- Select User --</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>{u.name} ({u.email}) - ID: {u.id}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Location Selector */}
                        <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden mt-6">
                            <div className="flex border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                                <button type="button" onClick={() => setLocationMode('map')} className={`flex-1 py-2 text-xs font-bold transition-colors ${locationMode === 'map' ? 'bg-geo-olive/10 text-geo-olive' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Map Picker</button>
                                <button type="button" onClick={() => setLocationMode('manual')} className={`flex-1 py-2 text-xs font-bold transition-colors ${locationMode === 'manual' ? 'bg-geo-olive/10 text-geo-olive' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800'}`}>Manual Entry</button>
                            </div>
                            <div className="p-4">
                                {locationMode === 'map' ? (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">{t.admin?.actuators?.location?.mapInstruction || "Click on the map to set the actuator's location."}</p>
                                        <div className="h-[250px] w-full rounded border border-gray-200 dark:border-slate-700 relative z-0 bg-black">
                                            <MapContainer center={defaultCenter} zoom={6} style={{ height: '100%', width: '100%' }}>
                                                <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution='&copy; ESRI' />
                                                <LocationPicker onLocationSelect={(loc) => setFormData({ ...formData, latitude: loc.lat.toString(), longitude: loc.lng.toString() })} />
                                                {formData.latitude && formData.longitude && (
                                                    <Marker position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]} icon={getMarkerIcon(1)} />
                                                )}
                                            </MapContainer>
                                        </div>
                                        <div className="flex gap-4 mt-2 text-xs font-mono text-gray-600 dark:text-gray-400">
                                            <span>Lat: {formData.latitude || 'Not set'}</span>
                                            <span>Lng: {formData.longitude || 'Not set'}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Latitude</label>
                                            <input type="number" step="any" value={formData.latitude} onChange={e => setFormData({...formData, latitude: e.target.value})} className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive" placeholder="e.g. 30.0444" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Longitude</label>
                                            <input type="number" step="any" value={formData.longitude} onChange={e => setFormData({...formData, longitude: e.target.value})} className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive" placeholder="e.g. 31.2357" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 mb-1">Location Name</label>
                                            <input type="text" value={formData.installed_location} onChange={e => setFormData({...formData, installed_location: e.target.value})} className="w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded p-2 text-sm text-geo-navy dark:text-white outline-none focus:border-geo-olive" placeholder="e.g. Zone A" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <button type="submit" disabled={submitting} className="bg-geo-olive hover:bg-geo-olive/90 text-white px-6 py-2 rounded font-bold transition-colors shadow flex items-center gap-2 disabled:opacity-50">
                                {submitting && <RefreshCw size={16} className="animate-spin" />}
                                {t.admin?.common?.save || "Save"}
                            </button>
                        </div>
                    </form>
                </Card>
            )}

            <div className="overflow-x-auto bg-white dark:bg-[#1B263B] rounded-lg border border-geo-navy/10 dark:border-white/10 shadow-sm">
                <table className="w-full text-left text-sm text-geo-navy dark:text-slate-300">
                    <thead className="bg-gray-50 dark:bg-slate-800/50 text-geo-navy/70 dark:text-slate-400 uppercase text-xs border-b border-geo-navy/10 dark:border-white/10">
                        <tr>
                            <th className="p-4">{t.admin?.actuators?.table?.id || "ID"}</th>
                            <th className="p-4">{t.admin?.actuators?.table?.name || "Device Name"}</th>
                            <th className="p-4">{t.admin?.actuators?.table?.type || "Type"}</th>
                            <th className="p-4" dir="ltr">{t.admin?.actuators?.table?.pubTopic || "Publish Topic"}</th>
                            <th className="p-4">{t.admin?.actuators?.table?.location || "Location"}</th>
                            <th className="p-4">{t.admin?.actuators?.table?.users || "Users"}</th>
                            <th className="p-4">{t.admin?.common?.actions || "Actions"}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-geo-navy/5 dark:divide-white/5">
                        {loading ? (
                            <tr><td colSpan="7" className="p-8 text-center text-gray-500">Loading actuators...</td></tr>
                        ) : actuators.length === 0 ? (
                            <tr><td colSpan="7" className="p-8 text-center text-gray-500">No actuators found.</td></tr>
                        ) : actuators.map(a => (
                            <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                                <td className="p-4 font-mono text-xs">{a.id}</td>
                                <td className="p-4 font-bold">{a.device_name}</td>
                                <td className="p-4">
                                    <span className="px-2 py-1 rounded bg-geo-olive/10 text-geo-olive text-xs font-semibold">{a.device_type}</span>
                                </td>
                                <td className="p-4 font-mono text-[10px] break-all">{a.mqtt_publish_topic || '-'}</td>
                                <td className="p-4 text-[11px]">
                                    {a.latitude && a.longitude ? (
                                        <div className="flex items-center gap-1 text-geo-navy/60 dark:text-slate-400">
                                            <MapPin size={12} /> {a.latitude}, {a.longitude}
                                        </div>
                                    ) : '-'}
                                </td>
                                <td className="p-4 text-xs">{(a.assigned_user_ids || []).join(', ')}</td>
                                <td className="p-4">
                                    <button onClick={() => handleDelete(a.id)} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors" title="Delete">
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default function AdminDashboardView({ adminId = 1, t, isRTL }) {
    const [subTab, setSubTab] = useState('overview'); // overview, users, approvals, iot

    return (
        <div dir={isRTL ? 'rtl' : 'ltr'} className="p-4 md:p-8 bg-geo-sand dark:bg-geo-navy rounded-xl border border-geo-navy/10 dark:border-white/10 min-h-[80vh] transition-colors duration-300 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between mb-8 items-start md:items-center border-b border-geo-navy/20 dark:border-white/20 pb-4 transition-colors">
                <div>
                    <h2 className="text-3xl font-bold text-geo-olive flex items-center gap-2">{t.admin?.commandCenter || "Admin Command Center"}</h2>
                    <p className="text-geo-navy dark:text-geo-sand opacity-70 text-sm mt-1">{t.admin?.commandCenterDesc || "Manage users, payments, and global sensor networks."}</p>
                </div>
                <div className="flex gap-2 bg-white dark:bg-geo-navy p-1 rounded-lg mt-4 md:mt-0 shadow-sm border border-gray-200 dark:border-white/10 transition-colors">
                    <button onClick={() => setSubTab('overview')} className={`px-4 py-2 rounded-md font-bold text-sm transition-all flex items-center gap-2 ${subTab === 'overview' ? 'bg-gray-100 dark:bg-transparent border-b-2 border-geo-copper text-geo-navy dark:text-white' : 'bg-transparent text-gray-500 dark:text-slate-400 hover:text-geo-copper hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        <Activity size={18} /> {t.admin?.tabs?.overview || "Overview"}
                    </button>
                    <button onClick={() => setSubTab('users')} className={`px-4 py-2 rounded-md font-bold text-sm transition-all flex items-center gap-2 ${subTab === 'users' ? 'bg-gray-100 dark:bg-transparent border-b-2 border-geo-copper text-geo-navy dark:text-white' : 'bg-transparent text-gray-500 dark:text-slate-400 hover:text-geo-copper hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        <Users size={18} /> {t.admin?.tabs?.users || "User Management"}
                    </button>
                    <button onClick={() => setSubTab('iot')} className={`px-4 py-2 rounded-md font-bold text-sm transition-all flex items-center gap-2 ${subTab === 'iot' ? 'bg-gray-100 dark:bg-transparent border-b-2 border-geo-copper text-geo-navy dark:text-white' : 'bg-transparent text-gray-500 dark:text-slate-400 hover:text-geo-copper hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        <Map size={18} /> {t.admin?.tabs?.iot || "Device Fleet"}
                    </button>
                    <button onClick={() => setSubTab('actuators')} className={`px-4 py-2 rounded-md font-bold text-sm transition-all flex items-center gap-2 ${subTab === 'actuators' ? 'bg-gray-100 dark:bg-transparent border-b-2 border-geo-copper text-geo-navy dark:text-white' : 'bg-transparent text-gray-500 dark:text-slate-400 hover:text-geo-copper hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        <Zap size={18} /> {t.admin?.tabs?.actuators || "Actuators"}
                    </button>
                    <button onClick={() => setSubTab('approvals')} className={`px-4 py-2 rounded-md font-bold text-sm transition-all flex items-center gap-2 ${subTab === 'approvals' ? 'bg-gray-100 dark:bg-transparent border-b-2 border-geo-copper text-geo-navy dark:text-white' : 'bg-transparent text-gray-500 dark:text-slate-400 hover:text-geo-copper hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                        <FileText size={18} /> {t.admin?.tabs?.approvals || "Billing Approvals"}
                    </button>
                </div>
            </div>

            <div className="mt-6">
                {subTab === 'overview' && <AdminOverview adminId={adminId} t={t} isRTL={isRTL} />}
                {subTab === 'users' && <AdminUsersDirectory adminId={adminId} t={t} isRTL={isRTL} />}
                {subTab === 'approvals' && <AdminApprovalsView adminId={adminId} t={t} isRTL={isRTL} />}
                {subTab === 'iot' && <AdminIoTMapView adminId={adminId} t={t} isRTL={isRTL} />}
                {subTab === 'actuators' && <AdminActuatorsView adminId={adminId} t={t} isRTL={isRTL} />}
            </div>
        </div>
    );
}
